/**
 * ServicesLogin.jsx — standalone login page for approved Startup Services vendors.
 * Lives at /services/login. Mirrors StudioLogin's shape but with the services accent.
 * On success → /vendor (the unified VendorDashboard).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '@/api';
import { toast } from 'sonner';
import { Lock, Spinner, ArrowRight, Buildings } from '@phosphor-icons/react';
import GoogleSignInButton, { GOOGLE_SIGNIN_ENABLED } from '@/components/GoogleSignInButton';
import OAuthIconButton, { OAUTH_PROVIDER_ENABLED } from '@/components/OAuthProviderButtons';

const ACCENT = '#10b981';

const ServicesLogin = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleVendorAuthSuccess = (user, token, fallbackEmail) => {
    if (user.vendor_type && user.vendor_type !== 'service') {
      toast.error("This account is a Startup Credit vendor. Use /credit/login instead.");
      return;
    }
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    onLogin?.(user, token);
    toast.success(`Welcome back, ${(user.full_name || fallbackEmail).split(' ')[0]}`);
    navigate('/vendor', { replace: true });
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!email || !password) return;
    setBusy(true);
    try {
      const r = await api.post('/vendor/auth/login', { email: email.trim(), password });
      handleVendorAuthSuccess(r.data.user || {}, r.data.token, email);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  const handleGoogleCredential = async (credential) => {
    setBusy(true);
    try {
      const r = await api.post('/vendor/auth/google', { credential });
      handleVendorAuthSuccess(r.data.user || {}, r.data.token, email || 'there');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Google sign-in failed');
    } finally {
      setBusy(false);
    }
  };

  // Resolve a Microsoft/Zoho redirect that just bounced back from OAuthCallback.jsx
  useEffect(() => {
    if (!location.state?.oauthResult) return;
    const { oauthResult } = location.state;
    handleVendorAuthSuccess(oauthResult.user || {}, oauthResult.token, email || 'there');
    window.history.replaceState({}, document.title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-background text-text-primary flex items-center justify-center p-4" data-testid="services-login-page">
      {/* Subtle background grid */}
      <div
        aria-hidden
        className="fixed inset-0 pointer-events-none opacity-40"
        style={{
          backgroundImage: 'linear-gradient(rgba(16,185,129,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(16,185,129,0.06) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
        }}
      />
      <div className="relative w-full max-w-md">
        <div className="flex items-center gap-2.5 mb-8">
          <span
            className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT}aa)` }}
          >
            <Buildings size={20} weight="fill" color="white" />
          </span>
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] font-bold" style={{ color: ACCENT }}>
              Founderboard
            </div>
            <div className="text-base font-heading font-bold leading-none" style={{ letterSpacing: '-0.01em' }}>
              Startup Services — Vendor
            </div>
          </div>
        </div>

        <h1 className="text-3xl sm:text-4xl font-heading font-bold leading-[1.05] mb-3" style={{ letterSpacing: '-0.02em' }}>
          Sign in to <span style={{ color: ACCENT }}>Services</span>
        </h1>
        <p className="text-sm text-text-muted mb-8 leading-relaxed">
          Manage your offerings, respond to founder intros, and keep your listing live on the
          Founderboard marketplace.
        </p>

        {(GOOGLE_SIGNIN_ENABLED || OAUTH_PROVIDER_ENABLED.microsoft || OAUTH_PROVIDER_ENABLED.zoho || OAUTH_PROVIDER_ENABLED.linkedin) && (
          <div className="mb-4">
            <div className="flex items-center justify-center gap-3">
              <GoogleSignInButton onCredential={handleGoogleCredential} disabled={busy} />
              <OAuthIconButton provider="microsoft" context="services" loginPath="/services/login" disabled={busy} />
              <OAuthIconButton provider="zoho" context="services" loginPath="/services/login" disabled={busy} />
              <OAuthIconButton provider="linkedin" context="services" loginPath="/services/login" disabled={busy} />
            </div>
            <div className="flex items-center gap-3 text-xs text-text-dim mt-4" aria-hidden="true">
              <div className="flex-1 h-px bg-border" />
              or
              <div className="flex-1 h-px bg-border" />
            </div>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4" data-testid="services-login-form">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.16em] font-bold text-text-muted mb-2">
              Work email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              data-testid="services-login-email"
              autoComplete="email"
              required
              className="w-full bg-surface border border-border rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-text-primary placeholder:text-text-faint"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.16em] font-bold text-text-muted mb-2">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              data-testid="services-login-password"
              autoComplete="current-password"
              required
              className="w-full bg-surface border border-border rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-text-primary placeholder:text-text-faint"
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="services-login-submit"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold text-white disabled:opacity-50 transition-opacity hover:opacity-90"
            style={{ backgroundColor: ACCENT }}
          >
            {busy ? <Spinner size={14} className="animate-spin" /> : <Lock size={14} weight="bold" />}
            Sign in
            {!busy && <ArrowRight size={14} weight="bold" />}
          </button>
        </form>

        <div className="mt-8 pt-6 border-t border-border text-[11px] text-text-dim leading-relaxed">
          New here? <a href="/services/apply" className="underline hover:text-text-primary">Apply as a services vendor</a>. Once approved, we&rsquo;ll email
          you a magic link to set your password. If you can&rsquo;t find it, check spam or ask sid@founderboard.io to resend.
        </div>
      </div>
    </div>
  );
};

export default ServicesLogin;
